import { useState } from 'react';
import { TextField, Button } from '@mui/material';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkersV2API } from '@/services/workersV2API';

type Props = {
  workerSkillId: number;
  expiresAt: string | null;
  daysUntilExpiration: number | null;
  expired: boolean;
  onUpdate: () => void;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateForInput = (dateString: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getExpirationBadge = (
  expiresAt: string | null,
  daysUntilExpiration: number | null,
  expired: boolean,
) => {
  if (!expiresAt) {
    return <Badge label="No Expiration" status="neutral" />;
  }

  if (expired || (daysUntilExpiration !== null && daysUntilExpiration < 0)) {
    return (
      <Badge
        label={`Expired ${Math.abs(daysUntilExpiration || 0)}d ago`}
        status="danger"
      />
    );
  }

  if (daysUntilExpiration === 0) {
    return <Badge label="Expires Today" status="danger" />;
  }

  if (daysUntilExpiration !== null && daysUntilExpiration <= 7) {
    return (
      <Badge label={`${daysUntilExpiration}d until expiry`} status="warning" />
    );
  }

  if (daysUntilExpiration !== null && daysUntilExpiration <= 30) {
    return (
      <Badge label={`${daysUntilExpiration}d until expiry`} status="theme" />
    );
  }

  return (
    <Badge
      label={`${daysUntilExpiration || 0}d until expiry`}
      status="neutral"
    />
  );
};

const ExpirationSection = ({
  workerSkillId,
  expiresAt,
  daysUntilExpiration,
  expired,
  onUpdate,
}: Props) => {
  const [expirationDate, setExpirationDate] = useState(
    formatDateForInput(expiresAt),
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      const expiresAtValue = expirationDate
        ? new Date(expirationDate).toISOString()
        : null;
      await WorkersV2API.updateWorkerSkillExpiration(
        workerSkillId,
        expiresAtValue,
      );
      onUpdate();
    } catch (err) {
      console.error('Error updating expiration:', err);
      alert('Failed to update expiration');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Clear expiration date?')) {
      return;
    }

    try {
      setSaving(true);
      await WorkersV2API.updateWorkerSkillExpiration(workerSkillId, null);
      setExpirationDate('');
      onUpdate();
    } catch (err) {
      console.error('Error clearing expiration:', err);
      alert('Failed to clear expiration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack vertical gap={12}>
      <Stack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Body weight="semibold">Expiration</Body>
        {getExpirationBadge(expiresAt, daysUntilExpiration, expired)}
      </Stack>

      {expiresAt && (
        <Stack vertical gap={4}>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Small color="lighter">Expires At:</Small>
            <Small>{formatDate(expiresAt)}</Small>
          </Stack>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Small color="lighter">Status:</Small>
            <Small color={expired ? 'danger' : 'lighter'}>
              {expired
                ? `Expired ${Math.abs(daysUntilExpiration || 0)} days ago`
                : daysUntilExpiration !== null && daysUntilExpiration >= 0
                  ? `${daysUntilExpiration} days until expiry`
                  : 'No expiration'}
            </Small>
          </Stack>
        </Stack>
      )}

      <Stack gap={8} style={{ alignItems: 'flex-end' }}>
        <TextField
          type="date"
          label="Expiration Date"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText="Leave empty to clear expiration"
        />
      </Stack>

      <Stack gap={8}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {expiresAt && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleClear}
            disabled={saving}
            size="small"
          >
            Clear
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default ExpirationSection;
